/**
 * variants.ts — several takes of one sound (2026-09-20). PURE.
 *
 * A footstep that is the same sample every time is heard as a loop within four
 * strides; game-audio practice is 4–8 variations per surface, picked at random,
 * with pitch and level jittered on top. The generator
 * (tools/asset-pipeline/gen-world-sounds.mjs) slices each ground's takes into
 * `foot_<ground>_<n>.mp3` and writes ONE manifest — `audio/variants.json` — that
 * says how many exist per logical key:
 *
 *     { "v": 1, "variants": { "foot_stone": 6, "sfx_impact_metal": 3 } }
 *
 * THE MANIFEST IS THE CONTRACT. The game asks for the LOGICAL key
 * (`foot_stone`); the runtime picks a take. No manifest, a 404, or a key it does
 * not list → everything behaves exactly as before (the synthesised stand-in, or
 * the single clip of that name). Nothing in the client hard-codes a count.
 */

export const VARIANT_MAX = 16;
const KEY_RE = /^[a-z][a-z0-9_]{1,63}$/;

/** Validate a fetched manifest into key → count. Anything malformed is dropped, never thrown (a bad file = no variants). */
export function parseVariantManifest(json: unknown): Map<string, number> {
  const out = new Map<string, number>();
  if (!json || typeof json !== 'object') return out;
  const m = json as { v?: unknown; variants?: unknown };
  if (m.v !== 1 || !m.variants || typeof m.variants !== 'object') return out;
  for (const [key, n] of Object.entries(m.variants as Record<string, unknown>)) {
    if (!KEY_RE.test(key) || typeof n !== 'number' || !Number.isInteger(n) || n < 1) continue;
    out.set(key, Math.min(VARIANT_MAX, n));
  }
  return out;
}

/** The file key of take `i` (1-based) of a logical key. */
export const variantKey = (key: string, i: number): string => `${key}_${i}`;

/**
 * A SHUFFLE BAG per key: every take plays once before any repeats, and the first of a new bag is never the last of
 * the old one. (Plain `random()` repeats a take back-to-back one time in `n` — with six footsteps, every third second.)
 */
export class VariantPicker {
  private readonly bags = new Map<string, { order: number[]; at: number; last: number }>();
  constructor(private readonly rng: () => number = Math.random) {}

  /** 1-based take index for `key` with `n` takes. */
  pick(key: string, n: number): number {
    if (n <= 1) return 1;
    let bag = this.bags.get(key);
    if (!bag || bag.order.length !== n) { bag = { order: [], at: n, last: 0 }; this.bags.set(key, bag); }
    if (bag.at >= bag.order.length) {
      const order = Array.from({ length: n }, (_, i) => i + 1);
      for (let i = n - 1; i > 0; i--) { const j = Math.floor(this.rng() * (i + 1)); [order[i], order[j]] = [order[j], order[i]]; }
      if (order[0] === bag.last) [order[0], order[n - 1]] = [order[n - 1], order[0]]; // never the same take across the seam
      bag.order = order; bag.at = 0;
    }
    const take = bag.order[bag.at++];
    bag.last = take;
    return take;
  }
}
